import { useMemo } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import {
  CheckCircle,
  Circle,
  AlertCircle,
  ChevronRight,
  TrendingUp,
  Lock,
  Unlock,
  Info
} from 'lucide-react'
import { clsx } from 'clsx'
import type { DealStage, StageCompletion, StageJudgement, CategoryProgress, UpgradeCondition } from '@/types'

interface StageProgressBarProps {
  stageJudgement?: StageJudgement
  stageCompletion?: StageCompletion
  onStageClick?: (stage: DealStage) => void
  onOverrideClick?: () => void
  onStageUpdate?: (stage: string) => void  // 阶段升级回调
  compact?: boolean
}

// 阶段配置
const STAGES: { key: DealStage; label: string; shortLabel: string }[] = [
  { key: 'lead', label: '线索', shortLabel: '线索' },
  { key: 'qualify', label: '验证', shortLabel: '验证' },
  { key: 'proposal', label: '方案', shortLabel: '方案' },
  { key: 'negotiate', label: '谈判/招投标', shortLabel: '谈判' },
  { key: 'closed_won', label: '签约', shortLabel: '签约' },
]

// 阶段英文到中文的映射（供外部使用）
export const STAGE_LABELS: Record<string, string> = {
  lead: '线索',
  qualify: '验证',
  proposal: '方案',
  negotiate: '谈判/招投标',
  closed_won: '签约',
  closed_lost: '丢单',
}

// 获取阶段中文标签
export function getStageLabel(stage: string): string {
  return STAGE_LABELS[stage] || stage
}

// 分类进度条子组件
function CategoryProgressBar({ 
  label, 
  progress, 
  colorClass 
}: { 
  label: string
  progress: CategoryProgress
  colorClass: string 
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 w-16 flex-shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={clsx('h-full rounded-full transition-all', colorClass)}
          style={{ width: `${progress.rate * 100}%` }}
        />
      </div>
      <span className="text-xs text-gray-600 w-8 flex-shrink-0 text-right">
        {progress.completed}/{progress.total}
      </span>
    </div>
  )
}

// 升级条件列表子组件
function UpgradeConditionList({ 
  conditions 
}: { 
  conditions: UpgradeCondition[] 
}) {
  if (conditions.length === 0) return null
  
  return (
    <div className="pt-2 border-t border-gray-100">
      <span className="text-xs font-medium text-gray-500 block mb-2">下一阶段条件</span>
      <div className="space-y-1">
        {conditions.map((condition, index) => (
          <div 
            key={index} 
            className="flex items-center gap-2 text-xs"
          >
            <span className={clsx(
              'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0',
              condition.isMet 
                ? 'bg-emerald-100 text-emerald-600' 
                : 'bg-gray-100 text-gray-400'
            )}>
              {condition.isMet ? '✓' : '○'}
            </span>
            <span className={clsx(
              condition.isMet ? 'text-emerald-700' : 'text-gray-600'
            )}>
              {condition.description}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// 阶段颜色
const STAGE_COLORS: Record<DealStage, string> = {
  lead: 'bg-slate-500',
  qualify: 'bg-blue-500',
  proposal: 'bg-indigo-500',
  negotiate: 'bg-purple-500',
  closed_won: 'bg-emerald-500',
  closed_lost: 'bg-gray-400',
}

/**
 * 阶段进度条组件
 * 
 * 展示 Deal 当前阶段和完成度，支持：
 * - 阶段可视化
 * - 完成度进度
 * - 人工覆盖状态
 * - 升级建议
 */
// 获取阶段来源显示信息
const getSourceDisplay = (source?: string, isOverridden?: boolean) => {
  if (isOverridden) {
    return { text: '人工覆盖', className: 'text-orange-600 font-medium' }
  }
  switch (source) {
    case 'override':
      return { text: '人工覆盖', className: 'text-orange-600 font-medium' }
    case 'user_declared':
    default:
      return { text: '用户设置', className: 'text-gray-600' }
  }
}

export default function StageProgressBar({
  stageJudgement,
  stageCompletion,
  onStageClick,
  onOverrideClick,
  onStageUpdate,
  compact = false,
}: StageProgressBarProps) {
  const currentStage = stageJudgement?.stageFinal || 'lead'
  const currentStageIndex = STAGES.findIndex(s => s.key === currentStage)

  // 计算各阶段状态
  const stageStatuses = useMemo(() => {
    return STAGES.map((_stage, index) => {
      if (index < currentStageIndex) {
        return 'completed'
      } else if (index === currentStageIndex) {
        return 'current'
      } else {
        return 'pending'
      }
    })
  }, [currentStageIndex])

  // 计算当前阶段完成度
  const currentStageCompletion = stageCompletion?.stages?.find(s => s.isCurrent)
  const overallProgress = stageCompletion?.overallProgress ?? 0

  if (compact) {
    // 紧凑模式：显示阶段标签 + 进度条
    return (
      <div className="flex items-center gap-2">
        <span className={clsx(
          'px-2 py-0.5 rounded-full text-xs font-medium text-white',
          STAGE_COLORS[currentStage]
        )}>
          {stageJudgement?.stageDisplay || STAGES[currentStageIndex]?.label}
        </span>
        <div className="flex items-center gap-1">
          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${overallProgress * 100}%` }}
              className={clsx('h-full rounded-full', STAGE_COLORS[currentStage])}
            />
          </div>
          <span className="text-xs text-gray-500">
            {Math.round(overallProgress * 100)}%
          </span>
        </div>
        {stageJudgement?.isOverridden && (
          <span title="人工覆盖">
            <Lock className="w-3 h-3 text-amber-500" />
          </span>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* 阶段进度条 */}
      <div className="relative">
        {/* 连接线 */}
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200" />
        
        {/* 阶段节点 */}
        <div className="relative flex justify-between">
          {STAGES.map((stage, index) => {
            const status = stageStatuses[index]
            const isClickable = onStageClick && status !== 'pending'

            return (
              <div
                key={stage.key}
                className={clsx(
                  'flex flex-col items-center',
                  isClickable && 'cursor-pointer'
                )}
                onClick={() => isClickable && onStageClick(stage.key)}
              >
                {/* 节点 */}
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className={clsx(
                    'relative z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all',
                    status === 'completed' && 'bg-emerald-500 text-white',
                    status === 'current' && clsx(STAGE_COLORS[stage.key], 'text-white ring-4 ring-offset-2', `ring-${stage.key === 'closed_won' ? 'emerald' : 'blue'}-200`),
                    status === 'pending' && 'bg-gray-200 text-gray-400'
                  )}
                >
                  {status === 'completed' ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : status === 'current' ? (
                    <span className="text-xs font-bold">{index + 1}</span>
                  ) : (
                    <Circle className="w-5 h-5" />
                  )}
                </motion.div>

                {/* 标签 */}
                <span className={clsx(
                  'mt-2 text-xs font-medium',
                  status === 'current' ? 'text-gray-900' : 'text-gray-500'
                )}>
                  {stage.label}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* 当前阶段详情 */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={clsx(
              'px-2.5 py-1 rounded-full text-sm font-medium text-white',
              STAGE_COLORS[currentStage]
            )}>
              {stageJudgement?.stageDisplay || STAGES[currentStageIndex]?.label}
            </span>
            
            {/* 置信度 */}
            {stageJudgement && (
              <span className={clsx(
                'text-xs px-2 py-0.5 rounded-full',
                stageJudgement.confidence >= 0.8 ? 'bg-emerald-100 text-emerald-700' :
                stageJudgement.confidence >= 0.6 ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              )}>
                {stageJudgement.confidenceDisplay || `${Math.round(stageJudgement.confidence * 100)}%`}
              </span>
            )}

            {/* 人工覆盖标记 */}
            {stageJudgement?.isOverridden && (
              <button
                onClick={onOverrideClick}
                className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700"
              >
                <Lock className="w-3 h-3" />
                人工覆盖
                {stageJudgement.overrideInfo?.needsConfirmation && (
                  <AlertCircle className="w-3 h-3 text-red-500" />
                )}
              </button>
            )}
          </div>

          {/* 升级建议 */}
          {stageCompletion?.canUpgrade && (
            <button className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium">
              <TrendingUp className="w-3 h-3" />
              可升级
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* 完成度详情 */}
        {stageCompletion && (
          <div className="space-y-2">
            {/* 总进度 */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 w-16">总进度</span>
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${overallProgress * 100}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  className={clsx('h-full rounded-full', STAGE_COLORS[currentStage])}
                />
              </div>
              <span className="text-xs font-medium text-gray-700 w-10 text-right">
                {Math.round(overallProgress * 100)}%
              </span>
            </div>

            {/* 当前阶段详细进度 */}
            {currentStageCompletion && (
              <div className="bg-white rounded p-3 space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">
                    {currentStageCompletion.stageDisplay} 阶段进度
                  </span>
                  <span className="text-xs font-medium text-gray-700">
                    {currentStageCompletion.confirmedSignals}/{currentStageCompletion.requiredSignals} 信号
                  </span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full', STAGE_COLORS[currentStage])}
                    style={{ width: `${currentStageCompletion.completionRate * 100}%` }}
                  />
                </div>
                
                {/* 分类进度条 */}
                {(currentStageCompletion.mustConfirmProgress || 
                  currentStageCompletion.mustFalsifyProgress || 
                  currentStageCompletion.observeProgress) && (
                  <div className="space-y-2 pt-2 border-t border-gray-100">
                    {currentStageCompletion.mustConfirmProgress && 
                     currentStageCompletion.mustConfirmProgress.total > 0 && (
                      <CategoryProgressBar
                        label="必须确认"
                        progress={currentStageCompletion.mustConfirmProgress}
                        colorClass="bg-emerald-500"
                      />
                    )}
                    {currentStageCompletion.mustFalsifyProgress && 
                     currentStageCompletion.mustFalsifyProgress.total > 0 && (
                      <CategoryProgressBar
                        label="必须证伪"
                        progress={currentStageCompletion.mustFalsifyProgress}
                        colorClass="bg-red-500"
                      />
                    )}
                    {currentStageCompletion.observeProgress && 
                     currentStageCompletion.observeProgress.total > 0 && (
                      <CategoryProgressBar
                        label="观察项"
                        progress={currentStageCompletion.observeProgress}
                        colorClass="bg-blue-500"
                      />
                    )}
                  </div>
                )}
                
                {/* 升级条件清单 */}
                {currentStageCompletion.upgradeConditions && 
                 currentStageCompletion.upgradeConditions.length > 0 && (
                  <UpgradeConditionList
                    conditions={currentStageCompletion.upgradeConditions}
                  />
                )}
                
                {currentStageCompletion.missingSignals.length > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    <span className="font-medium">待确认：</span>
                    {currentStageCompletion.missingSignals.slice(0, 3).join('、')}
                    {currentStageCompletion.missingSignals.length > 3 && '...'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 阶段升级建议（重构新增） */}
        {stageJudgement?.stageSuggestion && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm flex-1">
                <p className="text-blue-700 font-medium">
                  证据显示项目可能已进入更高阶段
                </p>
                <p className="text-blue-600 mt-1">
                  建议阶段：
                  <span className="font-semibold">
                    {stageJudgement.stageSuggestion.suggestedStageDisplay}
                  </span>
                </p>
                <p className="text-blue-500 text-xs mt-1">
                  {stageJudgement.stageSuggestion.reason}
                </p>
                {onStageUpdate && (
                  <button
                    onClick={() => onStageUpdate(stageJudgement.stageSuggestion!.suggestedStage)}
                    className="mt-2 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                  >
                    更新阶段
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 决策来源（重构修改：从 "AI 自动推断" 改为基于 source 字段显示） */}
        {stageJudgement && (
          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
            <span>
              决策来源：
              <span className={getSourceDisplay(stageJudgement.source, stageJudgement.isOverridden).className}>
                {getSourceDisplay(stageJudgement.source, stageJudgement.isOverridden).text}
              </span>
            </span>
            {stageJudgement.rulesMatched?.length > 0 && (
              <span className="text-gray-400">
                {stageJudgement.rulesMatched[0]?.description}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * 阶段覆盖对话框
 * 使用 Portal 渲染到 document.body，避免父容器 overflow/transform 影响定位
 */
export function StageOverrideDialog({
  isOpen,
  onClose,
  currentStage,
  onOverride,
  onClearOverride,
  isOverridden,
}: {
  isOpen: boolean
  onClose: () => void
  currentStage: DealStage
  onOverride: (stage: DealStage, reason?: string) => Promise<void>
  onClearOverride?: () => Promise<void>
  isOverridden: boolean
}) {
  if (!isOpen) return null

  const dialogContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
      onClick={(e) => {
        // 点击背景关闭
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {isOverridden ? '管理阶段覆盖' : '手动设置阶段'}
        </h3>

        <div className="space-y-4">
          {/* 阶段选择 */}
          <div className="grid grid-cols-3 gap-2">
            {STAGES.map((stage) => (
              <button
                key={stage.key}
                onClick={() => onOverride(stage.key)}
                className={clsx(
                  'p-3 rounded-lg border-2 text-center transition-all',
                  currentStage === stage.key
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <span className="text-sm font-medium">{stage.label}</span>
              </button>
            ))}
          </div>

          {/* 清除覆盖 */}
          {isOverridden && onClearOverride && (
            <button
              onClick={onClearOverride}
              className="w-full flex items-center justify-center gap-2 p-3 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
            >
              <Unlock className="w-4 h-4" />
              清除人工覆盖，恢复 AI 推断
            </button>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            取消
          </button>
        </div>
      </motion.div>
    </div>
  )

  // 使用 Portal 渲染到 body
  return createPortal(dialogContent, document.body)
}
